/**
 * State Adoption Report
 *
 * Automates the manual "FDIC and Registrant Information (state adoption)"
 * workbook: per-state .bank adoption computed from Monitor domain data joined
 * against the bulk-synced FDIC institution universe.
 *
 * Per state:
 * - # Banks:        active FDIC-insured institutions (bulk-synced daily)
 * - # Registrants:  unique registrants (deduped by registrant email)
 * - # .Bankers:     unique live registrants (deduped by FDIC cert)
 * - Potential New Registrants = banks - registrants
 * - Potential .Bankers        = registrants - .bankers
 * - % .Bankers in State      = .bankers / banks
 * - % Adoption (Customers)    = .bankers / registrants
 * - % of Market (Customers)   = registrants / banks
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { useMutation, usePaginatedQuery, useQuery } from 'convex/react';
import { format } from 'date-fns';
import { Check, FileSpreadsheet, Loader2, Pencil, RotateCcw, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useReportBuilderContactEmails } from '@/hooks/use-contacts';
import { getRegion } from '@/lib/region-utils';
import { isKnownStateCode, stateCodeToName, stateNameToCode } from '@/lib/us-states';
import { cn } from '@/lib/utils';
import { api } from '../../../../convex/_generated/api';
import { ChipGroup } from './components/chip-group';

const REPORT_PATH = '/app/reports/state-adoption';

// ============================================================================
// Types
// ============================================================================

interface AdoptionDomainRow {
  domain: string;
  effectiveStatus: string;
  registrantEmail: string | null;
  rddsName: string | null;
  registrantStateCode: string | null;
  cert: string | null;
  institution: string | null;
  charterStateName: string | null;
}

interface StateSummaryRow {
  stateCode: string;
  stateName: string;
  banks: number;
  registrants: number;
  bankers: number;
  potentialNewRegistrants: number;
  potentialBankers: number;
  adoptionOverall: number | null;
  adoptionCustomers: number | null;
  marketShareCustomers: number | null;
}

// ============================================================================
// Chip options
// ============================================================================

const BANKER_STATUS_OPTIONS = [
  { value: 'live', label: 'Live' },
  { value: 'redirect_in_zone', label: 'Redirect (In Zone)' },
  { value: 'redirect_out_of_zone', label: 'Redirect (Out)' },
  { value: 'parked', label: 'Parked' },
  { value: 'error', label: 'Error' },
  { value: 'not_in_zone', label: 'Not in Zone' },
];

const REGISTRANT_TYPE_OPTIONS = [
  { value: 'fdic', label: 'FDIC-insured' },
  { value: 'other', label: 'No FDIC cert (assn, SP, BHC)' },
];

const ATTRIBUTION_OPTIONS = [
  { value: 'registrant', label: 'Registrant State' },
  { value: 'charter', label: 'FDIC Charter State' },
] as const;

type Attribution = (typeof ATTRIBUTION_OPTIONS)[number]['value'];

// ============================================================================
// Formatting
// ============================================================================

function formatPercent(value: unknown) {
  if (value === null || value === undefined || value === '')
    return '—';
  const n = Number(value);
  if (!Number.isFinite(n))
    return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function formatCount(value: unknown) {
  return value === null || value === undefined ? '—' : Number(value).toLocaleString();
}

// ============================================================================
// Manual cell overrides
// ============================================================================

// The three count columns are editable in the grid. Edits persist in the
// stateAdoptionOverrides Convex table and are layered on top of the computed
// values; derived columns and the Total row recompute from them.
type EditableField = 'banks' | 'registrants' | 'bankers';

function overrideKey(stateCode: string, field: EditableField): string {
  return `${stateCode}|${field}`;
}

// ============================================================================
// Editable Count Cell
// ============================================================================

// Controlled inline editor rendered inside the AG Grid cell (same pattern as
// the monitor page's VelocityCell). Deliberately does NOT use AG Grid's
// built-in editing — all state and saving are handled here.
function EditableCountCell({
  value,
  isTotal,
  override,
  computedValue,
  onSave,
  onUndo,
}: {
  value: number;
  isTotal: boolean;
  override: { value: number; updatedAt: number } | undefined;
  computedValue: number | undefined;
  onSave: (value: number) => void;
  onUndo: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(String(value));
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed === '')
      return;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error('Enter a non-negative number');
      return;
    }
    if (parsed !== value)
      onSave(parsed);
  };

  if (isTotal) {
    return <span className="tabular-nums">{formatCount(value)}</span>;
  }

  if (isEditing) {
    return (
      <div className="flex h-full items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          min="0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter')
              handleSave();
            else if (e.key === 'Escape')
              setIsEditing(false);
          }}
          className="h-6 w-20 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-green-600 hover:bg-green-100 hover:text-green-700"
          onClick={handleSave}
        >
          <Check className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-red-600 hover:bg-red-100 hover:text-red-700"
          onClick={() => setIsEditing(false)}
        >
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex h-full items-center gap-1">
      <span className={cn('tabular-nums text-sm', override && 'font-semibold text-amber-700')}>
        {formatCount(value)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="size-5 p-0 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={startEditing}
        title="Edit value"
      >
        <Pencil className="size-3 text-muted-foreground" />
      </Button>
      {override && (
        <TooltipProvider>
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="size-5 p-0 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                onClick={onUndo}
              >
                <Undo2 className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <div className="font-medium">
                Manually edited
                {' '}
                {format(new Date(override.updatedAt), 'MMM d, h:mm a')}
              </div>
              <div className="text-muted-foreground">
                Computed value:
                {' '}
                {computedValue?.toLocaleString() ?? '—'}
              </div>
              <div>Click to undo</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function StateAdoptionReport() {
  const { setLabel, removeLabel } = useBreadcrumb();
  const [searchQuery, setSearchQuery] = useState('');
  const [bankerStatuses, setBankerStatuses] = useState<Set<string>>(() => new Set(['live']));
  const [registrantTypes, setRegistrantTypes] = useState<Set<string>>(
    () => new Set(['fdic', 'other']),
  );
  const [attribution, setAttribution] = useState<Attribution>('registrant');

  useEffect(() => {
    setLabel(REPORT_PATH, { name: 'State Adoption Report', path: REPORT_PATH });
    return () => removeLabel(REPORT_PATH);
  }, [setLabel, removeLabel]);

  // --- Data sources (same universe as the Report Builder) ---
  const cutoffMs = useMemo(() => Date.now() - 3 * 24 * 60 * 60 * 1000, []);
  const {
    results: domainStatuses,
    status: domainStatusesLoadStatus,
    loadMore: loadMoreDomainStatuses,
  } = usePaginatedQuery(
    api.monitor.getActiveDomainStatusesPaginated,
    { cutoffMs },
    { initialNumItems: 1000 },
  );
  useEffect(() => {
    if (domainStatusesLoadStatus === 'CanLoadMore') {
      loadMoreDomainStatuses(1000);
    }
  }, [domainStatusesLoadStatus, loadMoreDomainStatuses]);

  const overrides = useQuery(api.monitor.getAllActiveOverrides);
  const fdicByDomainRaw = useQuery(api.fdic.getFdicByDomain);
  const stateBankCounts = useQuery(api.fdic.getStateBankCounts);

  // Persisted manual cell edits
  const cellOverridesRaw = useQuery(api.stateAdoption.listOverrides);
  const upsertOverrideMutation = useMutation(api.stateAdoption.upsertOverride);
  const clearOverrideMutation = useMutation(api.stateAdoption.clearOverride);
  const clearAllOverridesMutation = useMutation(api.stateAdoption.clearAllOverrides);

  const cellOverrides = useMemo(() => {
    const map: Record<string, { value: number; updatedAt: number }> = {};
    for (const o of cellOverridesRaw ?? []) {
      map[overrideKey(o.stateCode, o.field as EditableField)] = {
        value: o.value,
        updatedAt: o.updatedAt,
      };
    }
    return map;
  }, [cellOverridesRaw]);

  const bankDomains = useMemo(
    () => [...new Set((domainStatuses || [])
      .filter((d) => d.domain.toLowerCase().endsWith('.bank'))
      .map((d) => d.domain.toLowerCase()))].sort((a, b) => a.localeCompare(b)),
    [domainStatuses],
  );
  const domainsLoaded = domainStatusesLoadStatus === 'Exhausted';
  const { data: contactEmailsByDomain } = useReportBuilderContactEmails(
    bankDomains,
    domainsLoaded && bankDomains.length > 0,
  );

  // --- Assemble US .bank domain rows ---
  const domainRows = useMemo((): AdoptionDomainRow[] => {
    if (!domainStatuses || !contactEmailsByDomain)
      return [];

    const overrideMap = new Map<string, string>();
    if (overrides) {
      for (const o of overrides) overrideMap.set(o.domain, o.statusOverride);
    }

    const fdicMap = new Map<string, NonNullable<typeof fdicByDomainRaw>[number]>();
    if (fdicByDomainRaw) {
      for (const item of fdicByDomainRaw) fdicMap.set(item.domain, item);
    }

    const contactMap = new Map<string, (typeof contactEmailsByDomain)[number]>();
    for (const row of contactEmailsByDomain) {
      contactMap.set(row.domain.toLowerCase(), row);
    }

    const now = Date.now();
    const rows: AdoptionDomainRow[] = [];

    for (const d of domainStatuses) {
      if (!d.domain.toLowerCase().endsWith('.bank'))
        continue;

      const contact = contactMap.get(d.domain.toLowerCase());
      // US & territories only (matches the workbook's "US Registrants" tab)
      if (getRegion(contact?.registrantCountry ?? null) !== 'US')
        continue;

      // Effective status (same precedence as the Report Builder)
      let effectiveStatus: string = d.currentStatus;
      const overrideStatus = overrideMap.get(d.domain);
      if (!d.inCurrentZoneFile) {
        effectiveStatus = 'not_in_zone';
      } else if (overrideStatus) {
        effectiveStatus = overrideStatus;
      } else if (d.graceExpiresAt && d.graceExpiresAt > now && d.highWaterStatus) {
        effectiveStatus = d.highWaterStatus;
      }

      const fdic = fdicMap.get(d.domain);
      // Multi-state contacts are comma-joined by the API; use the first
      const rawState = contact?.registrantState?.split(',')[0]?.trim() ?? null;

      rows.push({
        domain: d.domain,
        effectiveStatus,
        registrantEmail: contact?.registrantEmail ?? null,
        rddsName: contact?.rddsName ?? null,
        registrantStateCode: rawState ? rawState.toUpperCase() : null,
        cert: fdic?.primaryCert ?? null,
        institution: fdic?.primaryInstitution ?? null,
        charterStateName: fdic?.primaryInstitutionState ?? null,
      });
    }

    return rows;
  }, [domainStatuses, contactEmailsByDomain, overrides, fdicByDomainRaw]);

  const isLoading = !domainsLoaded || !contactEmailsByDomain || !fdicByDomainRaw || !stateBankCounts || !cellOverridesRaw;

  // Status chip counts (domain rows per effective status, before type filter)
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of domainRows) {
      counts[row.effectiveStatus] = (counts[row.effectiveStatus] || 0) + 1;
    }
    return counts;
  }, [domainRows]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { fdic: 0, other: 0 };
    for (const row of domainRows) {
      counts[row.cert ? 'fdic' : 'other']++;
    }
    return counts;
  }, [domainRows]);

  // --- Per-state rollup ---
  const { summaryRows, totalRow, uniqueRegistrants, uniqueBankers, baseCounts } = useMemo(() => {
    // State for a row under the current attribution mode. Charter attribution
    // falls back to the registrant's state for rows without an FDIC cert.
    // Contact state is free-form RDDS data (e.g. "SJ" for San Juan), so
    // anything that isn't a real state/territory code lands in Unknown.
    const registrantCodeFor = (row: AdoptionDomainRow): string =>
      isKnownStateCode(row.registrantStateCode) ? row.registrantStateCode! : 'ZZ';

    const stateCodeFor = (row: AdoptionDomainRow): string => {
      if (attribution === 'charter' && row.charterStateName) {
        return stateNameToCode(row.charterStateName) ?? registrantCodeFor(row);
      }
      return registrantCodeFor(row); // ZZ = Unknown bucket
    };

    const typedRows = domainRows.filter((row) =>
      registrantTypes.has(row.cert ? 'fdic' : 'other'),
    );

    // Unique registrants (deduped by registrant email; matches the workbook)
    const registrantsByKey = new Map<string, { state: string; row: AdoptionDomainRow; domains: string[]; anyBankerStatus: boolean }>();
    for (const row of typedRows) {
      const key = row.registrantEmail?.toLowerCase() || `domain:${row.domain}`;
      const existing = registrantsByKey.get(key);
      if (existing) {
        existing.domains.push(row.domain);
        existing.anyBankerStatus = existing.anyBankerStatus || bankerStatuses.has(row.effectiveStatus);
      } else {
        registrantsByKey.set(key, {
          state: stateCodeFor(row),
          row,
          domains: [row.domain],
          anyBankerStatus: bankerStatuses.has(row.effectiveStatus),
        });
      }
    }

    // Unique .bankers (rows in a selected status with an FDIC cert, deduped by cert)
    const bankersByCert = new Map<string, { state: string; row: AdoptionDomainRow; domains: string[] }>();
    for (const row of typedRows) {
      if (!row.cert)
        continue;
      if (!bankerStatuses.has(row.effectiveStatus))
        continue;
      const existing = bankersByCert.get(row.cert);
      if (existing) {
        existing.domains.push(row.domain);
      } else {
        bankersByCert.set(row.cert, { state: stateCodeFor(row), row, domains: [row.domain] });
      }
    }

    // Bank counts by state code (FDIC keys are full STNAME strings)
    const banksByCode = new Map<string, number>();
    if (stateBankCounts) {
      for (const [stname, count] of Object.entries(stateBankCounts.counts)) {
        const code = stateNameToCode(stname) ?? 'ZZ';
        banksByCode.set(code, (banksByCode.get(code) || 0) + count);
      }
    }

    const registrantCounts = new Map<string, number>();
    for (const r of registrantsByKey.values()) {
      registrantCounts.set(r.state, (registrantCounts.get(r.state) || 0) + 1);
    }
    const bankerCounts = new Map<string, number>();
    for (const b of bankersByCert.values()) {
      bankerCounts.set(b.state, (bankerCounts.get(b.state) || 0) + 1);
    }

    const allCodes = new Set<string>([
      ...banksByCode.keys(),
      ...registrantCounts.keys(),
      ...bankerCounts.keys(),
    ]);

    // Computed values per state, before manual edits (drives edit tooltips/undo)
    const computed = new Map<string, { banks: number; registrants: number; bankers: number }>();

    const rows: StateSummaryRow[] = [...allCodes].map((code) => {
      const base = {
        banks: banksByCode.get(code) ?? 0,
        registrants: registrantCounts.get(code) ?? 0,
        bankers: bankerCounts.get(code) ?? 0,
      };
      computed.set(code, base);

      // Manual edits override the computed counts
      const banks = cellOverrides[overrideKey(code, 'banks')]?.value ?? base.banks;
      const registrants = cellOverrides[overrideKey(code, 'registrants')]?.value ?? base.registrants;
      const bankers = cellOverrides[overrideKey(code, 'bankers')]?.value ?? base.bankers;

      return {
        stateCode: code,
        stateName: code === 'ZZ' ? 'Unknown' : (stateCodeToName(code) ?? code),
        banks,
        registrants,
        bankers,
        potentialNewRegistrants: banks - registrants,
        potentialBankers: registrants - bankers,
        adoptionOverall: banks > 0 ? bankers / banks : null,
        adoptionCustomers: registrants > 0 ? bankers / registrants : null,
        marketShareCustomers: banks > 0 ? registrants / banks : null,
      };
    });

    rows.sort((a, b) => b.registrants - a.registrants || a.stateName.localeCompare(b.stateName));

    const totals = { banks: 0, registrants: 0, bankers: 0 };
    for (const r of rows) {
      totals.banks += r.banks;
      totals.registrants += r.registrants;
      totals.bankers += r.bankers;
    }

    const total: StateSummaryRow = {
      stateCode: '__total',
      stateName: 'Total',
      banks: totals.banks,
      registrants: totals.registrants,
      bankers: totals.bankers,
      potentialNewRegistrants: totals.banks - totals.registrants,
      potentialBankers: totals.registrants - totals.bankers,
      adoptionOverall: totals.banks > 0 ? totals.bankers / totals.banks : null,
      adoptionCustomers: totals.registrants > 0 ? totals.bankers / totals.registrants : null,
      marketShareCustomers: totals.banks > 0 ? totals.registrants / totals.banks : null,
    };

    return {
      summaryRows: rows,
      totalRow: total,
      uniqueRegistrants: registrantsByKey,
      uniqueBankers: bankersByCert,
      baseCounts: computed,
    };
  }, [domainRows, registrantTypes, bankerStatuses, attribution, stateBankCounts, cellOverrides]);

  // --- Chip handlers ---
  const toggleBankerStatus = useCallback((value: string) => {
    setBankerStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(value))
        next.delete(value);
      else next.add(value);
      return next.size === 0 ? new Set(['live']) : next;
    });
  }, []);

  const toggleRegistrantType = useCallback((value: string) => {
    setRegistrantTypes((prev) => {
      const next = new Set(prev);
      if (next.has(value))
        next.delete(value);
      else next.add(value);
      return next.size === 0 ? new Set([value]) : next;
    });
  }, []);

  // --- Cell editing (persisted to the stateAdoptionOverrides table) ---
  const overrideCount = cellOverridesRaw?.length ?? 0;

  const handleUndoCell = useCallback(async (stateCode: string, field: EditableField) => {
    try {
      await clearOverrideMutation({ stateCode, field });
    } catch {
      toast.error('Failed to undo edit');
    }
  }, [clearOverrideMutation]);

  const handleResetEdits = useCallback(async () => {
    try {
      const result = await clearAllOverridesMutation({});
      toast.success(`Reset ${result.cleared} ${result.cleared === 1 ? 'edit' : 'edits'} to computed values`);
    } catch {
      toast.error('Failed to reset edits');
    }
  }, [clearAllOverridesMutation]);

  const handleSaveCell = useCallback(async (stateCode: string, field: EditableField, value: number) => {
    const base = baseCounts.get(stateCode)?.[field];
    try {
      // Typing the computed value back = removing the edit
      await (value === base
        ? clearOverrideMutation({ stateCode, field })
        : upsertOverrideMutation({ stateCode, field, value }));
    } catch {
      toast.error('Failed to save edit');
    }
  }, [baseCounts, clearOverrideMutation, upsertOverrideMutation]);

  // --- Grid columns (count columns use the inline EditableCountCell) ---
  const summaryColumns = useMemo((): ColDef<StateSummaryRow>[] => {
    const countColumn = (
      field: EditableField,
      headerName: string,
      extra?: Partial<ColDef<StateSummaryRow>>,
    ): ColDef<StateSummaryRow> => ({
      field,
      headerName,
      minWidth: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (p: ICellRendererParams<StateSummaryRow>) => {
        if (!p.data)
          return null;
        const isTotal = !!p.node.rowPinned;
        const stateCode = p.data.stateCode;
        return (
          <EditableCountCell
            value={p.data[field]}
            isTotal={isTotal}
            override={isTotal ? undefined : cellOverrides[overrideKey(stateCode, field)]}
            computedValue={baseCounts.get(stateCode)?.[field]}
            onSave={(value) => void handleSaveCell(stateCode, field, value)}
            onUndo={() => void handleUndoCell(stateCode, field)}
          />
        );
      },
      ...extra,
    });

    return [
      {
        field: 'stateName',
        headerName: 'State',
        minWidth: 180,
        pinned: 'left',
        sortable: true,
        filter: true,
      },
      countColumn('banks', '# Banks', { minWidth: 110 }),
      countColumn('registrants', '# Registrants', { sort: 'desc' }),
      countColumn('bankers', '# .Bankers', { minWidth: 120 }),
      {
        field: 'potentialNewRegistrants',
        headerName: 'Potential New Registrants',
        minWidth: 200,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => formatCount(p.value),
      },
      {
        field: 'potentialBankers',
        headerName: 'Potential .Bankers (Customers)',
        minWidth: 220,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => formatCount(p.value),
      },
      {
        field: 'adoptionOverall',
        headerName: '% .Bankers in State',
        minWidth: 170,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => formatPercent(p.value),
      },
      {
        field: 'adoptionCustomers',
        headerName: '% Adoption (Customers)',
        minWidth: 190,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => formatPercent(p.value),
      },
      {
        field: 'marketShareCustomers',
        headerName: '% of Market (Customers)',
        minWidth: 190,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => formatPercent(p.value),
      },
    ];
  }, [cellOverrides, baseCounts, handleUndoCell, handleSaveCell]);

  // --- Export (mirrors the workbook: SUMMARY + drill-down sheets) ---
  const handleExport = useCallback(() => {
    if (summaryRows.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const wb = utils.book_new();

      const summaryExport = [totalRow, ...summaryRows].map((r) => ({
        'State': r.stateName,
        '# Banks': r.banks,
        '# Registrants': r.registrants,
        '# .Bankers': r.bankers,
        'Potential New Registrants': r.potentialNewRegistrants,
        'Potential .Bankers (Customers)': r.potentialBankers,
        '% .Bankers in State': r.adoptionOverall ?? '',
        '% Adoption (Customers)': r.adoptionCustomers ?? '',
        '% of Market (Customers)': r.marketShareCustomers ?? '',
      }));
      const wsSummary = utils.json_to_sheet(summaryExport);
      wsSummary['!cols'] = [{ wch: 22 }, ...Array.from({ length: 8 }, () => ({ wch: 16 }))];
      utils.book_append_sheet(wb, wsSummary, 'SUMMARY');

      const registrantExport = [...uniqueRegistrants.values()].map((r) => ({
        'Registrant Email': r.row.registrantEmail ?? '',
        'RDDS Name': r.row.rddsName ?? '',
        'State': r.state === 'ZZ' ? 'Unknown' : (stateCodeToName(r.state) ?? r.state),
        'FDIC Cert': r.row.cert ?? '',
        'Institution': r.row.institution ?? '',
        'Has .Banker Status': r.anyBankerStatus ? 'Yes' : 'No',
        'Domains': r.domains.join(', '),
      }));
      const wsRegistrants = utils.json_to_sheet(registrantExport);
      wsRegistrants['!cols'] = [{ wch: 36 }, { wch: 28 }, { wch: 18 }, { wch: 12 }, { wch: 34 }, { wch: 16 }, { wch: 60 }];
      utils.book_append_sheet(wb, wsRegistrants, 'Unique Registrants');

      const bankerExport = [...uniqueBankers.values()].map((b) => ({
        'FDIC Cert': b.row.cert ?? '',
        'Institution': b.row.institution ?? '',
        'State': b.state === 'ZZ' ? 'Unknown' : (stateCodeToName(b.state) ?? b.state),
        'Registrant Email': b.row.registrantEmail ?? '',
        'Domains': b.domains.join(', '),
      }));
      const wsBankers = utils.json_to_sheet(bankerExport);
      wsBankers['!cols'] = [{ wch: 12 }, { wch: 34 }, { wch: 18 }, { wch: 36 }, { wch: 60 }];
      utils.book_append_sheet(wb, wsBankers, '.Bankers');

      const filename = `state_adoption_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
      writeFile(wb, filename);
      toast.success(`Exported ${summaryRows.length} states to ${filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export report');
    }
  }, [summaryRows, totalRow, uniqueRegistrants, uniqueBankers]);

  const bulkSyncMissing = stateBankCounts !== undefined && stateBankCounts.lastBulkSyncAt === null;

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">State Adoption Report</h1>
          <p className="text-sm text-muted-foreground">
            Per-state .bank adoption: FDIC-insured banks vs. unique registrants vs. live .bankers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {overrideCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleResetEdits()}
              className="gap-1.5 text-xs text-amber-700 hover:text-amber-800"
            >
              <RotateCcw className="size-3.5" />
              Reset
              {' '}
              {overrideCount}
              {' '}
              {overrideCount === 1 ? 'edit' : 'edits'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isLoading || summaryRows.length === 0}
            className="gap-2"
          >
            <FileSpreadsheet className="size-4" />
            Export
          </Button>
        </div>
      </div>

      {bulkSyncMissing && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="py-3">
            <p className="text-sm text-amber-800">
              <strong>FDIC universe not synced yet:</strong>
              {' '}
              per-state bank counts will be empty until the daily FDIC monitor completes its first bulk sync.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-4 border-dashed">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-x-8 gap-y-4">
              <ChipGroup
                label=".Banker Status"
                options={BANKER_STATUS_OPTIONS}
                selected={bankerStatuses}
                onToggle={toggleBankerStatus}
                counts={statusCounts}
              />
              <ChipGroup
                label="Registrant Type"
                options={REGISTRANT_TYPE_OPTIONS}
                selected={registrantTypes}
                onToggle={toggleRegistrantType}
                counts={typeCounts}
              />
              <ChipGroup
                label="State Attribution"
                options={ATTRIBUTION_OPTIONS}
                selected={attribution}
                onToggle={(v) => setAttribution(v as Attribution)}
              />
            </div>

            <div className="flex items-center justify-between gap-3 border-t pt-3">
              <span className="text-xs text-muted-foreground">
                {stateBankCounts?.lastBulkSyncAt
                  ? `${stateBankCounts.total.toLocaleString()} active FDIC institutions · synced ${format(new Date(stateBankCounts.lastBulkSyncAt), 'MMM d, h:mm a')}`
                  : 'FDIC institution universe pending first sync'}
                {' · '}
                {uniqueRegistrants.size.toLocaleString()}
                {' unique registrants · '}
                {uniqueBankers.size.toLocaleString()}
                {' unique .bankers · hover a count cell to edit'}
              </span>
              <Input
                type="search"
                placeholder="Search states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 w-48 text-xs"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary grid */}
      {isLoading
        ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading monitor and FDIC data...</span>
                </div>
              </CardContent>
            </Card>
          )
        : (
            <AGDataGrid
              columnDefs={summaryColumns}
              rowData={summaryRows}
              pinnedTopRowData={[totalRow]}
              quickFilterText={searchQuery}
              pagination={false}
              domLayout="autoHeight"
              getRowId={(params) => params.data.stateCode}
              overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No data</span>'
            />
          )}
    </Container>
  );
}

export default StateAdoptionReport;
